"use client"

import React, { useState } from "react";
import Link from "next/link";
import { useRouter } from 'next/navigation';
import axios from 'axios';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../components/ui/form"
import { Input } from "../../../components/ui/input"
import { Button } from "../../../components/ui/button"
import { FcGoogle } from "react-icons/fc";
import { PiEyeBold, PiEyeClosedBold } from "react-icons/pi";

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"

const API_URL = process.env.NEXT_PUBLIC_API_URL;


const formSchema = z.object({
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  email: z.string().email("Ingresa un correo electrónico válido"),
  password: z.string()
    .min(8, "La contraseña debe tener al menos 8 caracteres")
    .regex(/[A-Z]/, "La contraseña debe contener al menos una letra mayúscula")
    .regex(/[0-9]/, "La contraseña debe contener al menos un número")
    .regex(/[!@#$%^&*(),.?":{}|<>]/, "La contraseña debe contener al menos un caracter especial")
})

export const RegisterForm = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/api/v1/auth/register`, values);
      console.log('Registro exitoso:', response.data);
      router.push('/auth/login');
    } catch (error) {
      console.error('Error en el registro:', error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre</FormLabel>
                <FormControl>
                  <Input placeholder="Ingresa tu nombre" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="Ingresa tu correo" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <div className="flex items-center border rounded-lg">
                    <Input
                      placeholder="Ingresa contraseña"
                      type={!isPasswordVisible ? 'password' : 'text'}
                      {...field}
                      className="border-0 shadow-none"
                    />
                    <Button
                      variant="outline"
                      className="border-0 shadow-none"
                      type="button"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                    >
                      {!isPasswordVisible
                        ? <PiEyeClosedBold className="h-4 w-4" />
                        : <PiEyeBold className="h-4 w-4" />
                      }
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {error && <div className="text-red-500 text-sm">{error}</div>}

          <div className="my-3"></div>

          <Button type="submit" size={"lg"} className="w-full" disabled={isLoading}>
            {isLoading ? 'Registrando...' : 'Registrarme'}
          </Button>

          <Button variant="outline" size={"lg"} className="w-full">
            <FcGoogle className="mr-3 h-6 w-6" /> Ingresar con Google
          </Button>

          <div className="flex justify-center items-center space-x-2">
            <p className="text-sm">Ya tienes una cuenta?</p>
            <Button type="button" variant="link" className="text-purple-600 p-0 m-0 h-auto">
              <Link href="/auth/login">Ingresar</Link>
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
};